import { Injectable } from '@angular/core';
import { fromLegacyPromise, getAngularJSService } from '../ng-upgrade.utils';
import { Auth, LoggedUser, SignInResult, User } from '@core/models';

export interface AuthCredentials {
  userlogin: string;
  secret: string;
}

export interface MfaCodeInput {
  requestId: string;
  code: string;
}

export interface CerberusTokensStatus {
  hasKAccessToken: boolean;
  hasKRefreshToken: boolean;
}

export interface UserStatus {
  error?: string;
  code?: number | string;
}

interface LegacyHttpResponse<T> {
  data: T;
}

interface LegacyUserFactory {
  authenticate(userData: AuthCredentials): Promise<LegacyHttpResponse<SignInResult>>;
  consumeMfaCode(challenge: MfaCodeInput): Promise<LegacyHttpResponse<LoggedUser>>;
  authenticateCerberus(): Promise<LegacyHttpResponse<LoggedUser>>;
  logoutCerberus(): Promise<LegacyHttpResponse<void>>;
  checkTokensCerberus(): Promise<LegacyHttpResponse<CerberusTokensStatus>>;
  authenticateUserId(userId: string | number): Promise<LegacyHttpResponse<LoggedUser>>;
  refreshAuth(refreshToken: string): Promise<LegacyHttpResponse<{ auth: Auth }>>;
  mobileAuth(token: string): Promise<LegacyHttpResponse<LoggedUser>>;
  getUserData(token: string, userId: string | number): Promise<LegacyHttpResponse<User>>;
  setUserStatus(status: UserStatus | null): void;
  getUserStatus(): UserStatus | null;
}

interface LegacySecurityService {
  getLoggedUser(): LoggedUser | null;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private userFactory: LegacyUserFactory | null = null;
  private securityService: LegacySecurityService | null = null;

  private get factory(): LegacyUserFactory {
    if (this.userFactory) {
      return this.userFactory;
    }
    const svc = getAngularJSService<LegacyUserFactory>('userFactory');
    if (!svc) {
      throw new Error(
        'userFactory AngularJS não disponível. AngularJS pode não ter sido bootstrapped.'
      );
    }
    this.userFactory = svc;
    return this.userFactory;
  }

  private get security(): LegacySecurityService {
    if (this.securityService) {
      return this.securityService;
    }
    const svc = getAngularJSService<LegacySecurityService>('securityService');
    if (!svc) {
      throw new Error(
        'securityService AngularJS não disponível. AngularJS pode não ter sido bootstrapped.'
      );
    }
    this.securityService = svc;
    return this.securityService;
  }

  async authenticate(credentials: AuthCredentials): Promise<SignInResult> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.authenticate(credentials));
    return response.data;
  }

  async consumeMfaCode(challenge: MfaCodeInput): Promise<LoggedUser> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.consumeMfaCode(challenge));
    return response.data;
  }

  async authenticateCerberus(): Promise<LoggedUser> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.authenticateCerberus());
    return response.data;
  }

  async logoutCerberus(): Promise<void> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    await fromLegacyPromise(this.factory.logoutCerberus());
  }

  async checkTokensCerberus(): Promise<CerberusTokensStatus> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.checkTokensCerberus());
    return response.data;
  }

  async authenticateUserId(userId: string | number): Promise<LoggedUser> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.authenticateUserId(userId));
    return response.data;
  }

  async refreshAuth(refreshToken: string): Promise<Auth> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.refreshAuth(refreshToken));
    return response.data.auth;
  }

  async mobileAuth(token: string): Promise<LoggedUser> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.mobileAuth(token));
    return response.data;
  }

  async getUserData(token: string, userId: string | number): Promise<User> {
    // WRAPPER — reescrever com HttpClient quando userFactory for removida
    const response = await fromLegacyPromise(this.factory.getUserData(token, userId));
    return response.data;
  }

  setUserStatus(status: UserStatus | null): void {
    // WRAPPER — delega à userFactory (fonte única durante o híbrido)
    this.factory.setUserStatus(status);
  }

  getUserStatus(): UserStatus | null {
    // WRAPPER — delega à userFactory (fonte única durante o híbrido)
    return this.factory.getUserStatus();
  }

  getLoggedUser(): LoggedUser | null {
    return this.security.getLoggedUser();
  }
}
